import { getAdSlotsOnPage, inferDeviceTypeFromViewport } from './dom.js';
import { HEADER_BIDDER_TIMEOUT } from './events.js';
import { isFixedOrMultiSize } from './gpt/sizes.js';
import { resetPrebid } from './refresh/prebid.js';
import { debugMessage } from './debug.js';
import { transformPermutiveData } from './targeting/permutive.js';

/**
 * Get sizes for the ad slots.
 */
const buildSizeMapping = () => {
  const sizeMapping = {};

  // Parsing the DOM again to get the ad slot elements is slightly wasteful,
  // but less complex than passing those ad slot elements around, might revisit.
  getAdSlotsOnPage().forEach((slot) => {
    const slotID = slot.id;

    sizeMapping[slotID] = [];

    if (isFixedOrMultiSize(slot.sizes)) {
      sizeMapping[slotID].push({
        minViewPort: [0, 0],
        sizes: slot.sizes,
      });

      return;
    }

    slot.sizes.forEach((sizeMap) => {
      sizeMapping[slotID].push({
        minViewPort: sizeMap[0],
        sizes: sizeMap[1],
      });
    });
  });

  return sizeMapping;
};

/**
 * Callback invoked by the injected prebid script.
 *
 * It is called with a list of ad slots that need refreshing, if any.
 */
const callback = (slots, timedOut = false) => {
  debugMessage('Prebid: Got results', { slots });
  resetPrebid();

  if (window.pbjs.adserverCalled) return;

  window.pbjs.adserverCalled = true;

  googletag.pubads().refresh(slots);

  // the timedOut flag will be set if any of the bidders failed to
  // return within the timeout specified in the Rubicon Prebid wrapper
  // for details on the wrapper ask Mike Freeman, or Graham Martin
  // for details on the underlying PBJS library and the callback/timeout
  // see https://docs.prebid.org/dev-docs/publisher-api-reference/requestBids.html#:~:text=requestObj.-,bidsBackHandler,-Optional
  if (timedOut) {
    window.dispatchEvent(new Event(HEADER_BIDDER_TIMEOUT));
  }
};

/**
 * Set a targeting value 'deviceType' against every ad slot.
 */
const addDeviceTypeTargeting = (slots) => {
  const deviceType = inferDeviceTypeFromViewport();

  slots.forEach((slot) => {
    slot.setTargeting('deviceType', deviceType);
  });
};

/**
 * Attach event handlers for creating prebid related performance markers
 */
export const attachPerformanceEventListenersOnce = (() => {
  let called = false;

  return (pbjs) => {
    if (!called) {
      called = true;

      [
        ['auctionInit', 'pb_auction_init'],
        ['auctionEnd', 'pb_auction_end'],
        ['setTargeting', 'pb_set_targeting'],
        ['requestBids', 'pb_request_bids'],
        ['addAdUnits', 'pb_add_ad_units'],
      ].forEach(([event, marker]) => {
        pbjs.onEvent(event, () => performance.mark(marker));
      });

      [
        ['beforeBidderHttp', 'before_bidder_http'],
        ['bidRequested', 'bid_requested'],
        ['bidResponse', 'bid_response'],
        ['bidError', 'bid_error'],
        ['bidderDone', 'bidder_done'],
      ].forEach(([event, suffix]) => {
        switch (event) {
          case 'bidError':
            pbjs.onEvent(event, ({ bidderRequest }) => performance.mark(`pb_${bidderRequest.bidderCode.toLowerCase()}_${suffix}`));
            break;
          default:
            pbjs.onEvent(event, ({ bidderCode }) => performance.mark(`pb_${bidderCode.toLowerCase()}_${suffix}`));
        }
      });
    }
  };
})();

/**
 * Request bids.
 */
export const request = (slots) => {
  window.pbjs = window.pbjs || {};

  window.pbjs.que = window.pbjs.que || [];

  window.pbjs.que.push(() => {
    addDeviceTypeTargeting(slots);

    /* eslint-disable-next-line camelcase */
    const { permutive, page, p_standard } = transformPermutiveData();

    // The properties in this request object are those expected by the
    // header bidder script injected onto our page by AdOps and provided
    // by, for example, Rubicon.
    const requestObj = {
      callback,
      gptSlotObjects: slots,
      sizeMappings: buildSizeMapping(),
      data: {
        rubicon: {
          permutive,
          page: [page],
          p_standard,
        },
        appnexus: {
          permutive,
          page,
          p_standard,
        },
        ix: {
          permutive,
          page,
          p_standard,
        },
      },
    };

    if (!window.pbjs.rp) {
      debugMessage('Prebid: Not requesting bids as window.pbjs.rp is not defined');

      return;
    }

    attachPerformanceEventListenersOnce(window.pbjs);

    // The rubicon script expects window.pbjs.rp.requestBids to be called,
    // rather than window.pbjs.requestBids as defined in the Prebid docs.
    debugMessage('Prebid: Requesting bids', {
      requestObj,
      slots,
    });

    window.pbjs.rp.requestBids(requestObj);
  });
};
